import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonText } from '../../components/Skeleton';
import { formatDateTime } from '../../i18n';
import { toneForStatus, toneForVisibility } from './presentation';
import { useArticleSearch } from './queries';

interface CustomerKnowledgePanelProps {
  customerId: string;
}

/**
 * Das Wissen zu einem Kunden -- F4-25.
 *
 * Der Reiter in der Kundenakte. Er beantwortet die Frage, die beim Kunden
 * gestellt wird und nicht in der Wissensdatenbank: "Was wissen wir ueber
 * dieses Haus?" Umgekehrt gesucht -- vom Artikel zum Kunden -- gibt es den
 * Weg schon.
 *
 * **Die Liste enthaelt auch die allgemein gueltigen Artikel.** Sie gelten bei
 * diesem Kunden ebenfalls; sie wegzulassen hiesse, eine Antwort zu
 * verschweigen, die zutrifft. Unterscheidbar bleiben sie an der
 * Kennzeichnung.
 *
 * Die Treffer sehen aus wie in der Wissensdatenbank und tragen dieselben
 * Klassen: Dieselbe Sache soll an zwei Stellen nicht zweimal aussehen.
 */
export function CustomerKnowledgePanel({ customerId }: CustomerKnowledgePanelProps) {
  const { t, i18n } = useTranslation();
  const query = useArticleSearch({ customer_id: customerId });

  if (query.isError) return <ErrorNotice error={query.error} />;

  const articles = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <section className="card">
      <div className="card__header">
        <h2>{t('crm.knowledge')}</h2>
        <Link to="/app/crm/$customerId/knowledge" params={{ customerId }}>
          <Button>{t('crm.openKnowledge')}</Button>
        </Link>
      </div>

      {query.isPending ? <SkeletonText lines={6} /> : null}

      {query.isSuccess && articles.length === 0 ? (
        <EmptyState icon="knowledge" title={t('crm.noKnowledge')} hint={t('crm.noKnowledgeHint')} />
      ) : null}

      {articles.length > 0 ? (
        <ul className="result-list result-list--plain">
          {articles.map((article) => (
            <li key={article.id} className="result">
              <Link
                to="/app/crm/$customerId/knowledge"
                params={{ customerId }}
                className="result__title"
              >
                {article.title}
              </Link>
              {article.summary ? <span className="result__summary">{article.summary}</span> : null}
              <span className="result__meta">
                <span className="result__fact">
                  <span className={`result__dot result__dot--${toneForStatus(article.status)}`} />
                  {t(`kbStatus.${article.status}`)}
                </span>
                <span className="result__fact">
                  <span
                    className={`result__dot result__dot--${toneForVisibility(article.visibility)}`}
                  />
                  {t(`kbVisibility.${article.visibility}`)}
                </span>
                {(article.customer_ids ?? []).length === 0 ? (
                  <span className="result__fact">{t('knowledge.customersAll')}</span>
                ) : null}
                <span className="result__fact">
                  {t('knowledge.updatedAt', {
                    moment: formatDateTime(article.updated_at, i18n.language),
                  })}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Auch hier nachladbar: Ein Kunde mit vierzig Artikeln ist kein
          Sonderfall, und der Reiter zeigte sonst die ersten fuenfundzwanzig
          und schwiege ueber den Rest. */}
      {query.hasNextPage ? (
        <div className="row" style={{ marginTop: 'var(--space-4)' }}>
          <Button
            busy={query.isFetchingNextPage}
            onClick={() => {
              void query.fetchNextPage();
            }}
          >
            {t('knowledge.loadMore')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
